// The creature popup: a team member's details over the hunt, opened by the
// player from the team rail (its info button, or a click on the row). It only
// ever opens on a click, never by itself, and the hunt keeps running under it.
// Esc, the close button or a click outside closes it; arrow keys step through
// the team. Game updates re-render it in place. The full creature page stays
// one click away for everything else (stars, refine, relics, selling).
//
// The same popup shows another trainer's creature read-only: a click on one
// anywhere it appears (a player card's showcase, a chat card, a market row,
// Records, the Best Catch board, the Kinhall plinths) opens it with the owner's
// name on top and none of the owner's buttons. Arrow keys step through the
// creatures shown beside it (a showcase, a market page).
import { ascendReady } from './ascend.ts';
import { SPECIES, linePos } from '../shared/species.ts';
import { GROUND, AREA } from '../shared/grounds.ts';
import { traitOf } from '../shared/traits.ts';
import { statWeights } from '../shared/rating.ts';
import { creatureXpToNext, canFight, LEVEL_SLACK } from '../shared/xp.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, elChip, cname, nameHtml, summary } from './fmt.ts';
import { patch } from './dom.ts';
import { rollPills } from './rollpills.ts';
import { leadCreature, xpSource } from './hud.ts';
import { moveBarHtml } from './watch.ts';
import { ICON } from './icons.ts';
import type { ClientMsg, ListingDTO } from '../shared/protocol.ts';
import { relicName, relicLine } from '../shared/items.ts';
import { relicGradeBadge } from './badges.ts';
import { heldName } from '../shared/held.ts';
import { heldText } from './held.ts';
import { creatureArt } from './skins.ts';
import { honourMarks } from './provenance.ts';
import { ownedSnapshot, closeHoverCard } from './hovercard.ts';
import { openProfileCard } from './profilecard.ts';
import { playerLink } from './social.ts';
import { buyButton } from './market-view.ts';
import { curIcon, curWord } from './money.ts';

/** Someone else's creature as the popup shows it. */
interface View {
  c: CreatureDTO;
  owner: string;
  /** A market listing of it: the live one is looked up by id on each render (it can sell or end while open). */
  listing?: number;
  /** The listing's price as shared in chat, for when the market isn't loaded. */
  price?: { n: number; cur: 'coins' | 'gems' };
  /** Shown in chat (a show-off or a big catch). */
  chat?: boolean;
  /** The Exchange sells it, not a trainer: no trainer card. */
  house?: boolean;
}

let peekId: number | null = null;
/** Someone else's creature(s) on show, and which one. */
let other: { list: View[]; i: number } | null = null;
/** Waiting for the server to send someone else's creature (Records, the Best Catch board, the plinths). */
let waiting: string | null = null;
/** The creature the server was asked for is gone (released or sold away). */
let gone = false;
let layer: HTMLElement | null = null;
let before: HTMLElement | null = null;
let send: (m: ClientMsg) => void = () => {};
export function bindPeek(s: (m: ClientMsg) => void) { send = s; }

export const peekOpen = () => peekId !== null || !!other || waiting !== null || gone;
const myName = () => (S.trainer?.name ?? '').toLowerCase();

function show() {
  const opening = !peekOpen();
  if (opening) before = document.activeElement as HTMLElement | null;
  return opening;
}
function focusCard() { (layer?.querySelector('.peek') as HTMLElement | null)?.focus({ preventScroll: true }); }

export function openPeek(id: number) {
  if (!S.creatures.has(id)) return;
  show();
  other = null; waiting = null; gone = false;
  peekId = id;
  renderPeek();
  focusCard();
}

/** Someone else's creature, read-only; `list` is what arrow keys step through. Your own opens the normal popup. */
export function openOther(list: View[], i = 0) {
  const v = list[i];
  if (!v) return;
  if (v.owner.toLowerCase() === myName() && S.creatures.has(v.c.id)) { openPeek(v.c.id); return; }
  closeHoverCard();
  show();
  peekId = null; waiting = null; gone = false;
  other = { list, i };
  renderPeek();
  focusCard();
}

/** Ask the server for a creature only it can name (a record, a trainer's best catch, the latest Aura) and open it. */
export function viewCreature(of: 'record' | 'best' | 'legend', arg = '') {
  closeHoverCard();
  show();
  peekId = null; other = null; gone = false;
  waiting = `${of}:${arg}`;
  send({ t: 'creatureView', of, ...(of === 'record' ? { species: arg } : of === 'best' ? { name: arg } : {}) });
  renderPeek();
  focusCard();
}

/** The server's answer to askView. */
export function onCreatureView(m: { key: string; c: CreatureDTO | null; owner?: string }) {
  if (waiting === null || m.key !== waiting) return;
  if (!m.c || !m.owner) { waiting = null; gone = true; renderPeek(); return; }
  const was = before;
  waiting = null;
  openOther([{ c: m.c, owner: m.owner }]);
  before = was;
}

export function closePeek() {
  if (!peekOpen()) return;
  peekId = null; other = null; waiting = null; gone = false;
  if (layer) layer.hidden = true;
  before?.focus?.({ preventScroll: true });
  before = null;
}

/** Arrow keys: the previous or next creature in the team order shown on the rail, or in the list someone else's came from. */
function step(dir: number) {
  if (other) {
    const n = other.list.length;
    if (n < 2) return;
    other = { list: other.list, i: (other.i + dir + n) % n };
    renderPeek();
    return;
  }
  const t = S.trainer;
  if (!t || peekId === null) return;
  const lead = leadCreature();
  const order = lead && t.team.includes(lead.id) ? [lead.id, ...t.team.filter((x) => x !== lead.id)] : [...t.team];
  const i = order.indexOf(peekId);
  if (i < 0 || order.length < 2) return;
  openPeek(order[(i + dir + order.length) % order.length]);
}

function node() {
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'peek-scrim';
    layer.hidden = true;
    // A press that starts and ends outside the card closes it (a drag out of a text selection doesn't).
    let downOutside = false;
    layer.addEventListener('pointerdown', (e) => { downOutside = e.target === layer; });
    layer.addEventListener('click', (e) => {
      // A trainer's name opens their card, which sits under this popup: make way for it.
      if ((e.target as HTMLElement).closest?.('[data-player]')) { closePeek(); return; }
      if (downOutside && e.target === layer) closePeek();
      downOutside = false;
    });
    document.body.appendChild(layer);
    document.addEventListener('keydown', (e) => {
      if (!peekOpen()) return;
      const typing = (e.target as HTMLElement).closest?.('input, select, textarea');
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); closePeek(); }
      else if (!typing && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) { e.preventDefault(); e.stopImmediatePropagation(); step(e.key === 'ArrowLeft' ? -1 : 1); }
    }, true);
    window.addEventListener('hashchange', closePeek);
  }
  return layer;
}

const STATS = ['HP', 'Attack', 'Defense', 'Magic', 'Resist', 'Speed'];

function caughtLine(c: CreatureDTO) {
  const d = new Date(c.caughtAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (c.ground.startsWith('boss')) return `${d}, boss fight`;
  const g = GROUND[c.ground];
  return g ? `${d}, ${esc(AREA[g.area].name)}` : d;
}

function bodyHtml(c: CreatureDTO): string {
  const t = S.trainer!;
  const sp = SPECIES[c.species];
  const pos = linePos(c.species);
  const lead = leadCreature();
  const inTeam = t.team.includes(c.id), leading = lead?.id === c.id;
  const sits = !canFight(c, t.level);
  const hp = S.field?.teamHp[c.id];
  const need = creatureXpToNext(c.level);
  const src = xpSource(c.id, lead?.id);
  const tr = traitOf(c);
  const stats = statRows(c);
  const acts = [
    inTeam && !leading ? `<button class="btn small primary" data-act="lead" data-id="${c.id}"${c.listed || sits ? ' disabled' : ''}${sits ? ` title="Fights from trainer Lv ${c.level - LEVEL_SLACK}"` : ''}>Send to the field</button>` : '',
    ascendReady(c) ? `<button class="btn small gold" data-act="ascend" data-id="${c.id}">Ascend…</button>` : '',
    `<a class="btn small" href="#/creature/${c.id}">Full page</a>`,
    `<button class="btn small" data-act="calc-load" data-id="${c.id}">Calculator</button>`,
    `<button class="btn small" data-act="showcase" data-id="${c.id}" title="Post it to World chat as a card anyone can open">Show in chat</button>`,
    inTeam ? `<button class="btn small" data-act="team-remove" data-id="${c.id}"${t.team.length <= 1 ? ' disabled' : ''}>Move to storage</button>` : '',
  ].filter(Boolean).join('');
  return `<div class="peek" role="dialog" aria-modal="true" aria-label="${esc(cname(c))}" tabindex="-1" data-key="pk${c.id}">
    <button class="pk-x" data-act="peek-close" aria-label="Close" title="Close (Esc)">${ICON.close}</button>
    <div class="pk-head">
      <div class="pk-pic"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt="">${c.stars ? `<span class="pk-stars">${'★'.repeat(c.stars)}</span>` : ''}</div>
      <div class="grow">
        <h3 class="pk-name">${nameHtml(c)}</h3>
        <div class="pk-nick">${sp.elements.map(elChip).join('')}<span>${esc(sp.name)} · Stage ${pos.at} of ${pos.of}</span></div>
        <div class="pk-lv"><b>Lv ${c.level}</b><span class="muted">${leading ? 'On the field' : inTeam ? 'On the team' : 'In storage'}</span></div>
        ${hp ? `<div class="bar hp big"><i style="width:${pct(hp.hp / hp.maxHp)}%"></i><span>HP<em>${full(hp.hp)} / ${full(hp.maxHp)}</em></span></div>` : ''}
        <div class="bar xp big${leading || (src && !sits) ? '' : ' idle'}"><i style="width:${pct(c.xp / need)}%"></i><span>XP<em>${full(c.xp)} / ${full(need)}</em></span></div>
      </div>
    </div>
    ${rollPills(c, { noLevel: true })}
    <div class="pk-cols">
      <div class="pk-stats"><div class="pk-h"><span>Stats</span><span>Talent</span></div>${stats}</div>
      <div class="pk-side">
        <div class="pk-h"><span>Moves</span></div>${moveBarHtml(c)}
        <dl class="pk-facts">
          <dt>Trait</dt><dd>${tr ? `<b class="${tr.rare ? 'rare-name' : ''}">${tr.rare ? '◆ ' : ''}${esc(tr.name)}</b> <span class="muted">${esc(tr.desc)}</span>` : '—'}</dd>
          <dt>Caught</dt><dd>${caughtLine(c)}</dd>
        </dl>
      </div>
    </div>
    <div class="pk-acts">${acts}${t.team.length > 1 && inTeam ? `<span class="pk-nav"><button class="btn tiny" data-act="peek-step" data-id="-1" aria-label="Previous team member" title="Previous (←)">‹</button><button class="btn tiny" data-act="peek-step" data-id="1" aria-label="Next team member" title="Next (→)">›</button></span>` : ''}</div>
  </div>`;
}

const statRows = (c: CreatureDTO) => {
  const sp = SPECIES[c.species];
  const s = summary(c);
  const w = statWeights(c.species);
  const unused = sp.style === 'might' ? 3 : 1;
  const st = [s.stats.hp, s.stats.might, s.stats.guard, s.stats.focus, s.stats.will, s.stats.swift];
  const top = Math.max(st[0] / 2, ...st.slice(1));
  return st.map((v, i) => {
    const off = i === unused || !w[i];
    const scaled = i === 0 ? v / 2 : v;
    return `<div class="pk-st${off ? ' off' : ''}"><span>${STATS[i]}</span><b>${full(v)}</b><span class="pk-sb"><i style="width:${pct(scaled / top)}%"></i></span><em title="Talent ${c.rolls.ivs[i]} of 31">${c.rolls.ivs[i]}</em></div>`;
  }).join('');
};
const pct = (x: number) => (Math.max(0, Math.min(1, x)) * 100).toFixed(1);

/** Someone else's creature: what it is and whose, with no owner's buttons. */
function otherHtml(v: View, at: number, of: number): string {
  const c = v.c;
  const sp = SPECIES[c.species];
  const pos = linePos(c.species);
  const need = creatureXpToNext(c.level);
  const tr = traitOf(c);
  const live: ListingDTO | undefined = v.listing !== undefined ? S.market?.listings.find((l) => l.id === v.listing) : undefined;
  const who = v.house ? `<b>${esc(v.owner)}</b>` : playerLink(v.owner, `<b>${esc(v.owner)}</b>`);
  const price = live ? { n: live.unit || live.price, cur: live.cur ?? 'coins' as const } : v.price;
  const band = v.listing !== undefined
    ? `<span>For sale by ${who}</span>${price ? `<span class="pk-price"><img src="${curIcon(price.cur)}" alt="">${full(price.n)} ${curWord(price.cur, price.n)}</span>` : ''}`
    : `<span>${v.chat ? `Shown in chat by ${who}` : `${who}’s creature`}</span>`;
  const facts = [
    `<dt>Trait</dt><dd>${tr ? `<b class="${tr.rare ? 'rare-name' : ''}">${tr.rare ? '◆ ' : ''}${esc(tr.name)}</b> <span class="muted">${esc(tr.desc)}</span>` : '—'}</dd>`,
    `<dt>Caught</dt><dd>${caughtLine(c)}${c.catcher && c.catcher.toLowerCase() !== v.owner.toLowerCase() ? ` <span class="muted">by ${esc(c.catcher)}</span>` : ''}</dd>`,
    c.relic ? `<dt>Relic</dt><dd>${relicGradeBadge(c.relic, 'sm')}<b>${esc(relicName(c.relic))}</b> <span class="muted">${esc(relicLine(c.relic))}</span></dd>` : '',
    c.held ? `<dt>Holds</dt><dd><b>${esc(heldName(c.held))}</b> <span class="muted">${esc(heldText(c.held))}</span></dd>` : '',
    honourMarks(c) ? `<dt>Honours</dt><dd class="pv">${honourMarks(c)}</dd>` : '',
  ].join('');
  // A listing: buy right here on the market page; elsewhere, go to it. A sold or ended one says so.
  const sold = v.listing !== undefined && S.page === 'market' && !!S.market && !live;
  const deal = v.listing === undefined ? ''
    : sold ? '<span class="muted pk-sold">No longer for sale</span>'
    : live && S.page === 'market' ? buyButton(live)
    : `<a class="btn small primary" href="#/market/${v.listing}">View listing</a>`;
  const acts = [deal, v.house ? '' : `<button class="btn small" data-act="peek-owner" data-id="${esc(v.owner)}">${esc(v.owner)}’s card</button>`].filter(Boolean).join('');
  return `<div class="peek other" role="dialog" aria-modal="true" aria-label="${esc(`${v.owner}: ${cname(c)}`)}" tabindex="-1" data-key="po${c.id}">
    <button class="pk-x" data-act="peek-close" aria-label="Close" title="Close (Esc)">${ICON.close}</button>
    <div class="pk-band">${band}</div>
    <div class="pk-head">
      <div class="pk-pic"><img src="${spriteUrl(creatureArt(c), c.rolls.aura)}" alt="">${c.stars ? `<span class="pk-stars">${'★'.repeat(c.stars)}</span>` : ''}</div>
      <div class="grow">
        <h3 class="pk-name">${nameHtml(c)}</h3>
        <div class="pk-nick">${sp.elements.map(elChip).join('')}<span>${esc(sp.name)} · Stage ${pos.at} of ${pos.of}</span></div>
        <div class="pk-lv"><b>Lv ${c.level}</b></div>
        <div class="bar xp big"><i style="width:${pct(c.xp / need)}%"></i><span>XP<em>${full(c.xp)} / ${full(need)}</em></span></div>
      </div>
    </div>
    ${rollPills(c, { noLevel: true })}
    <div class="pk-cols">
      <div class="pk-stats"><div class="pk-h"><span>Stats</span><span>Talent</span></div>${statRows(c)}</div>
      <div class="pk-side">
        <div class="pk-h"><span>Moves</span></div>${moveBarHtml(c, true)}
        <dl class="pk-facts">${facts}</dl>
      </div>
    </div>
    <div class="pk-acts">${acts}${of > 1 ? `<span class="pk-nav"><span class="muted pk-of">${at + 1} of ${of}</span><button class="btn tiny" data-act="peek-step" data-id="-1" aria-label="Previous" title="Previous (←)">‹</button><button class="btn tiny" data-act="peek-step" data-id="1" aria-label="Next" title="Next (→)">›</button></span>` : ''}</div>
  </div>`;
}

/** Waiting for the server, or the creature it was asked for is gone. */
function waitHtml(text: string): string {
  return `<div class="peek other wait" role="dialog" aria-modal="true" aria-label="Creature" tabindex="-1" data-key="pw">
    <button class="pk-x" data-act="peek-close" aria-label="Close" title="Close (Esc)">${ICON.close}</button>
    <p class="pk-wait">${text}</p></div>`;
}

/** Re-render in place: on open, and after every game update while it is open. */
export function renderPeek() {
  if (other || waiting !== null || gone) {
    const n = node();
    patch(n, other ? otherHtml(other.list[other.i], other.i, other.list.length) : waiting !== null ? waitHtml('Loading…') : waitHtml('That creature has been released or traded away since.'));
    n.hidden = false;
    return;
  }
  if (peekId === null) return;
  const c = S.creatures.get(peekId);
  if (!c || !S.trainer) { closePeek(); return; }
  const n = node();
  patch(n, bodyHtml(c));
  n.hidden = false;
}

/** Clicks inside the popup that belong to it. */
export function peekClick(act: string, id: string): boolean {
  if (act === 'peek') { const n = Number(id); if (Number.isFinite(n)) openPeek(n); return true; }
  if (act === 'peek-close') { closePeek(); return true; }
  if (act === 'peek-step') { step(Number(id)); return true; }
  if (act === 'peek-owner') { closePeek(); openProfileCard(id); return true; }
  return false;
}

/**
 * What a click on someone else's creature opens, by what the element carries: data-cview (a record, a best catch or
 * the latest Aura, asked of the server), data-snap (a registered snapshot with its owner), data-showcase (a chat
 * card) or data-listing-id (a market row). False when it isn't someone else's creature we can show.
 */
function openFrom(el: HTMLElement): boolean {
  const ds = el.dataset;
  if (ds.cview !== undefined) {
    const [of, ...rest] = ds.cview.split(':');
    if (of !== 'record' && of !== 'best' && of !== 'legend') return false;
    viewCreature(of, rest.join(':'));
    return true;
  }
  if (ds.snap !== undefined) {
    const hit = ownedSnapshot(ds.snap);
    if (!hit) return false;
    // Its neighbours (the rest of a showcase) are what the arrow keys step through.
    const sibs = [...(el.parentElement?.children ?? [])].map((x) => (x as HTMLElement).dataset?.snap).filter((k): k is string => !!k);
    const list = sibs.map((k) => ownedSnapshot(k)).filter((x): x is NonNullable<typeof x> => !!x).map((x) => ({ c: x.c, owner: x.owner }));
    const i = list.findIndex((x) => x.c === hit.c);
    openOther(i >= 0 ? list : [hit], Math.max(0, i));
    return true;
  }
  if (ds.showcase !== undefined) {
    const b = S.broadcasts.find((x) => x.sid === Number(ds.showcase) && x.creature);
    if (!b?.creature || !b.who) return false;
    openOther([{ c: b.creature, owner: b.who, chat: !b.listing, ...(b.listing ? { listing: b.listing.id, price: { n: b.listing.unit || b.listing.price, cur: b.listing.cur ?? 'coins' } } : {}) }]);
    return true;
  }
  if (ds.listingId !== undefined) {
    const view = (id: number): View | null => {
      const l = S.market?.listings.find((x) => x.id === id);
      return l && l.kind === 'creature' && l.snapshot ? { c: l.snapshot as CreatureDTO, owner: l.seller, listing: l.id, ...(l.house ? { house: true } : {}) } : null;
    };
    const hit = view(Number(ds.listingId));
    if (!hit) return false;
    const list = [...(el.parentElement?.querySelectorAll<HTMLElement>(':scope > [data-listing-id]') ?? [])].map((x) => view(Number(x.dataset.listingId))).filter((x): x is View => !!x);
    const i = list.findIndex((x) => x.listing === hit.listing);
    openOther(i >= 0 ? list : [hit], Math.max(0, i));
    return true;
  }
  return false;
}

/** Clicks (taps) on someone else's creature anywhere open the read-only popup. A long-press still shows the hover card. */
export function mountPeekLinks() {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const t = e.target as HTMLElement;
    const el = t.closest?.('[data-cview],[data-snap],[data-showcase],[data-listing-id]') as HTMLElement | null;
    if (!el || el.closest('.peek, .hc')) return;
    // A button or link inside it (Buy, the seller's name, View listing) does its own thing.
    const ctl = t.closest('a, button, input, select, textarea, label, [data-player], [data-act]');
    if (ctl && ctl !== el && el.contains(ctl)) return;
    if (openFrom(el)) { e.preventDefault(); e.stopPropagation(); }
  }, true);
  // Enter on a focused one opens it too.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || peekOpen()) return;
    const el = e.target as HTMLElement;
    if (!el.matches?.('[data-cview],[data-snap],[data-showcase],[data-listing-id]') || el.closest('.peek, .hc')) return;
    if (openFrom(el)) { e.preventDefault(); e.stopPropagation(); }
  });
}
